import { useEffect, useState } from 'react';

import { getRoutesDirections } from '@/data/libraries/Routes';
import { StatusCodes } from '@/data/libraries/Status';
import { ConfigNumberParameters } from '@/data/libraries/ConfigParameters';
import { ShippingTypesCodes } from '@/data/libraries/ShippingTypes';

import { saveAs } from 'file-saver';

import { type Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

import { useScreenSize, type ScreenSize } from '@/ui/hooks/useScreenSize';

import { Button, Col, Container, Image, Row, Table } from 'react-bootstrap';
import { useAppSelector } from '@/ui/store/helperRedux';
import './style.css';

import OrdersController from '@/controllers/OrdersController';

import { IBreadcrumbs } from '@/domain/interfaces/IBreadcrumbs';
import { IOrderData, IOrderStateFlow, IOrderTaxes } from '@/domain/interfaces/IOrder';
import { IStatusDetails } from '@/domain/interfaces/IStatus';

import lastPageName from '@/domain/utils/lastPageName';
import currencyFormat from '@/domain/utils/currencyFormat';
import { getCSSVariableValue } from '@/domain/utils/CssExtractVariable';
import deliveryDate from '@/domain/utils/deliveryDate';
import { translate } from '@/domain/utils/translate';
import capitalize from '@/domain/utils/capitalize';

import Icon from '@/ui/assets/Icon';
import ErrorImage from '@/ui/assets/ErrorImage';

import Breadcrumbs from '@/ui/components/Breadcrumbs';
import IconList from '@/ui/components/IconList';
import InfoModal from '@/ui/components/modals/InfoModal';
import AlertModal from '@/ui/components/modals/AlertModal';

/**
 * Componente de detalles de seguimiento de orden.
 *
 * @param order - La orden de compra.
 * @returns Un componente de React que muestra los detalles de seguimiento de una orden de compra.
 *
 */

const TrackingOrderDetails = () => {


  /** VARIABLES DE ENTORNO */
  const URL_IMAGES: string = process.env.REACT_APP_URL_IMAGES ?? '';
  /**Gif de imagen URL */
  const DOWNLOAD_IMAGE_URL: string = `${URL_IMAGES}/client/doc-download.png`;
  /** Hook para trabajar con document location  */
  const location: Location = useLocation();

  const buttonVariant = getCSSVariableValue('--button-variant');
  const linkVariantButton = getCSSVariableValue('--icon-variant-light');

  /** Hook para navegar entre páginas   */
  const navigate: NavigateFunction = useNavigate();

  /** Estado Redux del usuario */
  const userLogued = useAppSelector((state) => state.userState);

  /**Estado de la orden */
  const [order, setOrder] = useState<IOrderData | null>(location.state.order ?? null);

  /**Controlador para obtener las órdenes */
  const { getOrderPdf, getOrdersByCode, getOrderFlow } = OrdersController();

  /** Rutas URLs */
  const routesDirections = getRoutesDirections();

  /** Listado de breadcrumbs de la página */
  const breadcrumbsList: IBreadcrumbs[] = [
    { label: translate('breadcrumbs.home'), url: routesDirections.MAIN_ROUTE },
    { label: lastPageName(location.pathname), url: location.pathname },
  ];

  /*Estados finales de la orden si no se encuentra el icono es un cubo por defecto y el nombre estado desconocido*/
  const errorStates = [StatusCodes.ORDER_CANCELED];

  /**Estados para deshabilitar el boton de desacarga de PDF de orden */
  const downloadOrderDisabled = [
    StatusCodes.ORDER_CANCELED,
    StatusCodes.ORDER_ERROR,
    StatusCodes.ORDER_PENDING,
    StatusCodes.ORDER_DELIVERED,
  ].includes(order?.status ?? 0);

  /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();

  /** Variable que define si es entorno mobile o desktop */
  const isMobile: boolean = width < maxLargeWidth;

  /**Modal de loading */
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);

  /** Estado inicial del modal de alertas de descarga de orden*/
  const [alertDownload, setAlertDownload] = useState<boolean>(false);

  /** Estado fechas entrega de orden*/
  const [deliveryDateFormated, setDeliveryDateFormated] = useState<string>('');

  /**flujo de estados cuando cambia la orden*/
  const [orderFlow, setOrderFlow] = useState<IOrderStateFlow[]>([]);

  /**Mapa de estados de la orden */
  const statusMap = {
    [StatusCodes.ORDER_PENDING]: { icon: 'ico-pending', name: capitalize(translate('general.status.order_pending')) },
    [StatusCodes.ORDER_PROCESSING]: { icon: 'ico-processing', name: capitalize(translate('general.status.order_processing')) },
    [StatusCodes.ORDER_SENT]: { icon: 'ico-sent', name: capitalize(translate('general.status.order_sent')) },
    [StatusCodes.ORDER_DELIVERED]: { icon: 'ico-national-shipment', name: capitalize(translate('general.status.order_delivered')) },
    [StatusCodes.ORDER_BRANCH_PICKUP]: { icon: 'ico-store', name: capitalize(translate('general.status.order_in_office')) },
    [StatusCodes.ORDER_ERROR]: { icon: 'ico-danger', name: capitalize(translate('general.status.order_error')) },
    [StatusCodes.ORDER_CANCELED]: { icon: 'ico-danger', name: capitalize(translate('general.status.order_canceled')) },
  };

  /*Mapa de estados de la orden a pasos */
  const statusToStepMap = {
    [StatusCodes.ORDER_PENDING]: 0,
    [StatusCodes.ORDER_PROCESSING]: 1,
    [StatusCodes.ORDER_SENT]: 2,
    [StatusCodes.ORDER_BRANCH_PICKUP]: 2,
    [StatusCodes.ORDER_DELIVERED]: 3,
    [StatusCodes.ORDER_ERROR]: 3,
    [StatusCodes.ORDER_CANCELED]: 3,
  };

  /*extrae el nombre de los estados de la orden*/
  const getStatusName = (status: number): string => {
    return statusMap[status]?.name || StatusCodes.ORDER_ERROR;
  };

  /*extrae el nombre de los estados de la orden*/
  const getStatusIcon = (status: number): string => {
    return statusMap[status]?.icon || 'ico-cube';
  };

  /*Extrae los detalles del estado de la orden*/
  const getStatusDetails = (status: number): { icon: string; name: string } => statusMap[status] || { icon: 'ico-cube', name: 'Estado desconocido' };

  /** Evento que muestra y oculta el modal de carga */
  const showLoading = (): void => {
    setShowDownloadModal(!showDownloadModal);
  };
  /** Estado para deshabilitar el botón de descarga durante la operación */
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  /** Función para descargar el archivo */
  const handleDownload = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();

    if (downloadOrderDisabled || isDownloading) return;
    setIsDownloading(true);
    // Mostrar el modal de carga
    setShowDownloadModal(true);
    /** Solicita por medio del controlador el template file del inventario */
    const response: Blob | null = await getOrderPdf(order?.ordCodeNum);
    /**if que controla si la respuesta es null o no para mostrar el modal de alerta de error de descarga o descargar el archivo respectivamente */
    if (response === null) {
      setShowDownloadModal(false);
      setAlertDownload(true);
    } else {
      setShowDownloadModal(false);

      /** Descarga el archivo */
      saveAs(response, `order_${order?.ordCodeNum}.pdf`);
    }
    setIsDownloading(false);
  };

  /**Función que obtiene la orden por codigo */
  const getOrderBack = async (orderCode: string = ''): Promise<void> => {
    let searchParam = orderCode;
    if (order) {
      searchParam = order?.ordCodeNum;
    }

    try {
      const fetchedOrder = await getOrdersByCode(searchParam);
      if (fetchedOrder) {
        setOrder(fetchedOrder);

        /** Se imprime la fecha estimada de entrega si es necesario */
        setDeliveryDateFormated(deliveryDate(
          fetchedOrder?.ordInitialDeliveryDate ?? '',
          fetchedOrder?.ordFinalDeliveryDate ?? '',
          fetchedOrder?.status ?? 0
        ));

        /** Establece el flujo de estados */
        setOrderFlow(fetchedOrder.ordStatusFlow ?? []);
      }

    } catch (error) {
      console.error(error);
    }

  };

  const fetchOrderFlow = async (ordCodeNum: number): Promise<void> => {
    try {
      const flow: IOrderStateFlow[] = await getOrderFlow(ordCodeNum);
      if (flow) {
        setOrderFlow(flow);
      }
    } catch (error) {
      console.error(error);
    }
  };

  /** Si no viene la orden en el state puede consultar por queryParam order */
  useEffect(() => {

    if (!order) {
      const params = new URLSearchParams(location.search);
      const orderParam = params.get('order');

      if (orderParam) {
        getOrderBack(orderParam);
      } else {
        navigate(routesDirections.TRACKING_ROUTE);

      }
    } else {
      fetchOrderFlow(order.id);

    }

  }, [order, location]);

  const redirectNewRequest = () => {
    if (userLogued.user.isLogued) {
      navigate(routesDirections.PURCHASES_ROUTE);
    } else {
      navigate(routesDirections.TRACKING_ROUTE);
    }
  }

  useEffect(() => {
    getOrderBack();
  }
    , []);

  /** CONSTANTES DE TEXTO SEGUIMIENTO */
  const PREPARING_TEXT: string = capitalize(translate('general.status.order_pending'));
  const PROCESSING_TEXT: string = capitalize(translate('general.status.processing'));
  const BRANCH_PICKUP_TEXT: string = capitalize(translate('general.status.order_in_office'));
  const SHIPPED_TEXT: string = capitalize(translate('general.status.order_sent'));
  const DELIVERED_TEXT: string = capitalize(translate('general.status.order_delivered'));
  const CURRENT_STATUS_TEXT: string = translate('sales.current_status_label');
  const ID_ORDER_TEXT = `${isMobile ? 'ID:' : `${translate('sales.purchase_id')}:`}`;

  const ORDER_DETAILS_TEXT = translate('sales.order_details_text');
  const ORDER_TITLE_TEXT = translate('sales.order_title_text');
  const SEARCH_OTHER_ORDER_TEXT = translate('sales.search_other_order_text');
  const RESUME_PURCHASE_TEXT: string = translate('sales.resume_purchase_text');
  const BUYER_TEXT: string = translate('sales.buyer_text');
  const NAME_TEXT: string = translate('sales.name_text');
  const IDENTITY_CARD_TEXT: string = translate('sales.identity_card_text');
  const PHONE_TEXT: string = translate('sales.phone_text');
  const EMAIL_TEXT: string = translate('sales.email_text');
  const SUB_TOTAL_TEXT: string = translate('sales.sub_total_text');
  const SHIPPING_TEXT: string = translate('sales.shipping_text');
  const TOTAL_TAX_TEXT: string = translate('sales.total_tax_text');
  const TOTAL_TEXT: string = translate('sales.total_text');
  const EXEMPT_TEXT: string = translate('sales.exempt_text');

  const PRODUCTS_TEXT: string = order?.ordTotalProducts === 1 ? translate('product.product') : translate('product.products');
  const ADDED_PRODUCTS_TEXT: string = order?.ordTotalProducts === 1 ? translate('sales.added_label') : translate('sales.added_plural_label');

  /*CONSTANTES DE TEXTO DETALLE DE PRODUCTOS*/
  const ORDER_TEXT = translate('sales.order_text');
  const ORDER_TYPE_TEXT = translate('sales.order_type_text');
  const ORDER_DELIVERY_TEXT = translate('sales.order_delivery_text');
  const DETAIL_TEXT = translate('sales.detail_text');
  const QUANTITY_PRODUCT_TEXT = translate('sales.quantity_product_text');
  const PRICE_PRODUCT_TEXT = translate('sales.price_product_text');
  const LOADING_TEXT: string = translate('sales.download_loading_text');
  const ERROR_DOWNLOAD_MESSAGE: string = translate('sales.error_download_message');
  const ERROR_TEXT: string = translate('sales.error_text');
  const CAUSE_CANCELATION_TEXT: string = translate('sales.cause_cancelation_text');

  /** Componente de Form Wizard de la orden */
  const StatusComponent = (): JSX.Element => {
    /**Toma el nombre del estado de error */
    const getErrorStatusText: string | null = errorStates.includes(order?.status ?? 0) ? getStatusDetails(order?.status ?? 0).name : null;
    const currentStep: number = statusToStepMap[order?.status ?? 0];

    /**Pasos para envío a domicilio */
    const nationalShipmentSteps = [
      {
        icon: 'ico-pending',
        text: PREPARING_TEXT,
        isActive: currentStep >= 0,
        date: orderFlow.find((flow) => flow.stateId === StatusCodes.ORDER_PENDING)?.dateStateChange ?? '',
      },
      {
        icon: 'ico-processing',
        text: PROCESSING_TEXT,
        isActive: currentStep >= 1,
        date: orderFlow.find((flow) => flow.stateId === StatusCodes.ORDER_PROCESSING)?.dateStateChange ?? '',
      },
      {
        icon: 'ico-national-shipment',
        text: SHIPPED_TEXT,
        isActive: currentStep >= 2,
        date: orderFlow.find((flow) => flow.stateId === StatusCodes.ORDER_SENT)?.dateStateChange ?? '',
      },
      {
        icon: 'ico-selection',
        text: DELIVERED_TEXT,
        isActive: currentStep > 2,
        date: orderFlow.find((flow) => flow.stateId === StatusCodes.ORDER_DELIVERED)?.dateStateChange ?? '',
      },
    ];

    /**Pasos para retiro en tienda*/
    const pickUpSteps = [
      {
        icon: 'ico-pending',
        text: PREPARING_TEXT,
        isActive: currentStep >= 0,
        date: orderFlow.find((flow) => flow.stateId === StatusCodes.ORDER_PENDING)?.dateStateChange ?? '',
      },
      {
        icon: 'ico-processing',
        text: PROCESSING_TEXT,
        isActive: currentStep >= 1,
        date: orderFlow.find((flow) => flow.stateId === StatusCodes.ORDER_PROCESSING)?.dateStateChange ?? '',
      },
      {
        icon: 'ico-store',
        text: BRANCH_PICKUP_TEXT,
        isActive: currentStep >= 2,
        date: orderFlow.find((flow) => flow.stateId === StatusCodes.ORDER_BRANCH_PICKUP)?.dateStateChange ?? '',
      },
      {
        icon: 'ico-selection',
        text: DELIVERED_TEXT,
        isActive: currentStep > 2,
        date: orderFlow.find((flow) => flow.stateId === StatusCodes.ORDER_DELIVERED)?.dateStateChange ?? '',
      },
    ];



    /**Función que renderiza los Tabs de los status */
    const renderStatusTabs = (steps: IStatusDetails[]) => {
      const colSize = Math.floor(12 / steps.length);

      return (
        <Col xs={11} className='d-flex justify-content-center align-items-center mx-auto'>
          {steps.map(({ icon, text, date, isActive }, index) => (
            <Col key={`status-tab-${index}`} xs={colSize} className={`position-relative status-order-tab p-0 ${isActive ? 'active' : ''}`}>
              <Row className={`text-center mt-4 status-order-text g-0 ${isActive ? 'active' : ''}`}>
                <Col className='line'></Col>
                <Col className='position-absolute'>
                  <Icon name={icon} classMain='icon z-1 p-2 rounded-circle svg-primary-4 ' />
                </Col>
              </Row>
              <Row className={`text-center mt-4 status-order-text g-0 text-secondary-1 ${isActive ? 'active' : ''}`}>
                <span>{text}</span>
              </Row>

              {date ? (
                <Row lg={12} className={`d-flex justify-content-center text-center mt-2  g-0  text-tertiary-3 ${isActive ? 'active' : ''}`}>
                  <Col lg={5} xs={11} className='border rounded-4 '>
                    <span>{date.split(' ')[0]}</span>
                    <Col className='d-flex align-items-center justify-content-center '>
                      <Icon name='ico-clock' classMain='d-flex' size={12}></Icon>
                      <span className='ps-2'>{date.split(' ')[1]}</span>
                    </Col>
                  </Col>
                </Row>
              ) : (
                <Row lg={12} xs={5} className={`${isMobile ? 'mh-52-px' : 'mh-55-px'}`}></Row>
              )}
            </Col>
          ))
          }
        </Col >
      );
    };

    /**Variable para traer las tabs renderizada como JSX */
    let statusTabs: JSX.Element;

    if (getErrorStatusText) {
      statusTabs = renderStatusTabs(
        orderFlow.map((flow) => {
          const status = statusMap[flow.stateId] || { icon: 'ico-cube', name: StatusCodes.ORDER_ERROR };
          return {
            icon: status.icon,
            text: status.name,
            date: flow.dateStateChange,
            isActive: true,
          };
        }),
      );
    } else if (order?.ordShippingType?.id === ShippingTypesCodes.NATIONAL_SHIPMENT) {
      statusTabs = renderStatusTabs(nationalShipmentSteps);
    } else {
      statusTabs = renderStatusTabs(pickUpSteps);
    }


    return (
      <Row className='d-flex flex-column g-0 bg-secondary-5 shadow rounded py-3 px-4'>
        <Col>
          <Row className='align-items-center mb-1 px-3 py-2 rounded bg-secondary-1 text-primary-4'>
            <Col lg={3}>
              <span className='p-regular  fw-bold pe-3 text-primary-4'>{CURRENT_STATUS_TEXT}</span>
              <IconList
                iconName={getStatusIcon(order?.status ?? 0)}
                title={getStatusName(order?.status ?? 0)}
                classMain='svg-primary-4'
                classTitle='fw-bold text-primary-4 ps-2'
              />
            </Col>
            <Col lg={5}>
              <span className='p-regular text-primary-4'>{order?.status === StatusCodes.ORDER_BRANCH_PICKUP ? order.deliveryAddress : ''}</span>
            </Col>
            <Col lg={4} className={`${isMobile ? 'text-start mt-2 ' : 'text-end '}`}>
              {(order?.status === StatusCodes.ORDER_SENT || order?.status === StatusCodes.ORDER_PROCESSING) &&
                (<span className='p-regular text-primary-4'>{deliveryDateFormated}</span>)
              }
            </Col>
          </Row>
          {order?.status === StatusCodes.ORDER_ERROR ? (
            <Row className='rounded bg-primary-5 py-4 border border-danger mt-3 '>
              <Row className='g-0'>
                <Col className='text-center'>
                  <IconList iconName='ico-danger' title={translate('sales.pay_error')} classIcon='svg-complementary-2 pe-2' />
                  <p className='text-secondary-1 mt-3'>{ERROR_TEXT}</p>
                </Col>
              </Row>
            </Row>
          ) : (
            <>
              {order?.status === StatusCodes.ORDER_CANCELED ? (
                <>
                  <Col className='my-2'>
                    <span className='text-secondary-1'>{CAUSE_CANCELATION_TEXT}</span>
                  </Col>
                  <Row className='rounded bg-primary-5 pb-5 pt-2 border tracking-order mb-3 ps-4 '>
                    <Col className='g-0 text-secondary-1 paragraph fw-bold'>
                      {orderFlow.map((flow: IOrderStateFlow, index: number) => (
                        <Row key={`flow-${index}`} className='g-0 text-break pe-4'>
                          <Col>{flow.cause}</Col>
                        </Row>
                      ))}
                    </Col>
                  </Row>
                  <Row className='rounded bg-primary-5 py-4 border tracking-order pb-3'>
                    <Row className='g-0'>{statusTabs}</Row>
                  </Row>
                </>
              ) : (
                <Row className='rounded bg-primary-5 py-4 border tracking-order pb-3'>
                  <Row className='g-0'>{statusTabs}</Row>
                </Row>
              )}
            </>
          )}
        </Col>
      </Row>
    );
  };

  /**Componente de resumen de compra */
  const ResumeComponent = (): JSX.Element => (
    <Row className='g-0 my-2'>
      <Row className='g-0 bg-secondary-5 shadow rounded  '>
        <Col lg={12} className='d-flex justify-content-center '>
          <Col lg={10} xs={11}>
            <Col className='text-start my-3  '>
              <span className={`text-secondary-1  ${isMobile ? 'h5' : 'h3'} `}>{RESUME_PURCHASE_TEXT}</span>
            </Col>
          </Col>
        </Col>

        <Col lg={12} xs={12} className='d-flex justify-content-center  '>
          <Col lg={10} xs={11} className='d-flex justify-content-center border mb-5'>
            <Col lg={7} xs={11} className=' d-flex flex-column justify-content-between my-4'>
              <Col>
                <p className='mb-1 fw-bold text-secondary-2'>{BUYER_TEXT}</p>
              </Col>
              <Col className='d-flex justify-content-between mb-1'>
                <span className='text-secondary-2 text-nowrap pe-2'>&bull; {NAME_TEXT}</span>

                <span className='text-secondary-2 text-end'>{order?.customerName}</span>
              </Col>
              <Col className='d-flex justify-content-between mb-1'>
                <span className='text-secondary-2 text-nowrap'>&bull; {IDENTITY_CARD_TEXT}</span>
                <span className='ps-2 text-secondary-2 text-end text-truncate'>{`${order?.customerId.value ? order?.customerId.value : 'S/I'}`}</span>
              </Col>
              <Col className='d-flex justify-content-between mb-1'>
                <span className='text-secondary-2 text-nowrap'>&bull; {PHONE_TEXT}</span>
                <span className='text-secondary-2 text-end'>{`+${order?.customerPhone}`}</span>
              </Col>
              <Col className='d-flex justify-content-between '>
                <span className='text-secondary-2 text-nowrap'>&bull; {EMAIL_TEXT}</span>
                <span className='text-secondary-2 text-end'>{order?.customerEmail}</span>
              </Col>
              <hr />
              <Col className='my-2 d-flex justify-content-between'>
                <p className='m-0 fw-bold text-secondary-2'>
                  {SUB_TOTAL_TEXT} ({order?.ordTotalProducts} {PRODUCTS_TEXT}):
                </p>
                <span className='fw-bold text-secondary-2'>{currencyFormat(order?.ordTotalWithoutTax, ConfigNumberParameters.DECIMALS_ALLOWED)}</span>
              </Col>
              <Col className='d-flex justify-content-between mb-1'>
                <span className='text-secondary-2 text-nowrap'>
                  &bull; {PRODUCTS_TEXT} ({order?.ordTotalProducts} {ADDED_PRODUCTS_TEXT}):
                </span>
                <span className='text-secondary-2'>{currencyFormat(order?.ordTotalWithoutTax, ConfigNumberParameters.DECIMALS_ALLOWED)}</span>
              </Col>
              <Col className='d-flex justify-content-between mb-1'>
                <span className='text-secondary-2 text-nowrap'>&bull; {SHIPPING_TEXT}</span>
                <span className='text-secondary-2'>{currencyFormat(order?.ordTotalShipping, ConfigNumberParameters.DECIMALS_ALLOWED)}</span>
              </Col>
              <hr />
              <Col className='my-2 d-flex justify-content-between'>
                <p className='m-0 fw-bold text-secondary-2'>{TOTAL_TAX_TEXT}</p>
                <span className=' text-secondary-2 fw-bold'>
                  {order?.ordTotalTaxes === 0 || null ? (
                    <span className='fw-bold'>{EXEMPT_TEXT}</span>
                  ) : (
                    currencyFormat(order?.ordTotalTaxes, ConfigNumberParameters.DECIMALS_ALLOWED)
                  )}
                </span>
              </Col>
              {order?.orderTaxes.map((tax: IOrderTaxes) => (
                <Col key={`${order?.ordCodeNum}-${tax.name}`} className='d-flex justify-content-between mb-1'>
                  <span className='text-secondary-2 text-nowrap'>&bull; {tax.name}</span>
                  <span className='text-secondary-2'>{`$${tax.amount}`}</span>
                </Col>
              ))}
              <hr />
              <Col className='d-flex justify-content-between mt-2'>
                <span className='h4 text-secondary-2'>{TOTAL_TEXT}</span>
                <span className='h4 text-secondary-2'>{currencyFormat(order?.totalAmount, ConfigNumberParameters.DECIMALS_ALLOWED)}</span>
              </Col>
            </Col>
          </Col>
        </Col>
      </Row>
    </Row>
  );

  /**Componente de detalles de la orden */
  const OrderDetailsComponent = (): JSX.Element => (
    <Row className='g-0 bg-secondary-5 shadow rounded my-2 '>
      <Col lg={12} className='d-flex justify-content-center '>
        <Col lg={10} xs={11}>
          <Col className='text-start my-3  '>
            <span className={`text-secondary-1  ${isMobile ? 'h5' : 'h3'} `}>{ORDER_TEXT}</span>
          </Col>
          <Col lg={12} className='d-flex justify-content-between mb-2  '>
            <span className='text-secondary-1 '>{ORDER_TYPE_TEXT}</span>
            <span className='text-secondary-1 '>{order?.ordShippingType ? order?.ordShippingType.title : ''}</span>
          </Col>
          <Col lg={12} className='d-flex justify-content-between mb-2 '>
            <span className='text-secondary-1  '>{ORDER_DELIVERY_TEXT}</span>
            <span className='text-secondary-1 text-end'>{order?.deliveryAddress}</span>
          </Col>
        </Col>
      </Col>

      <Col className='d-flex justify-content-center'>
        <Col lg={10} xs={11} className='mb-5'>
          <Col lg={12} xs={12} className='my-4'>
            <Col>
              <Table className='border-0 bg-secondary-5 table no-hover'>
                <thead className='bg-secondary-1'>
                  <tr className='text-primary-4 '>
                    <th></th>
                    <th>
                      <p className='p-0 m-0 py-1'>{DETAIL_TEXT}</p>
                    </th>
                    <th>
                      <p className='text-center p-0 m-0 py-1'>{TOTAL_TEXT}</p>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {order?.orderProducts.map((product) => (
                    <tr className='border-bottom border-dark' key={`${order?.ordCodeNum}-${product.title}`}>
                      <td className='p-0'>
                        <Col className='text-center d-flex justify-content-center pt-5'>
                          <div className='image-crud-container rounded rounded-3 '>
                            <Image
                              className='rounded rounded-3 image-full'
                              src={product.image}
                              alt={''}
                              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                e.currentTarget.src = ErrorImage;
                              }}
                            />
                          </div>
                        </Col>
                      </td>

                      <td className='text-start '>
                        <Col>
                          <p>{product.title}</p>
                          <span className='paragraph pe-1'>
                            <b>{product.firstAttributeKey}</b>
                            {`:${product.firstAttributeValue},`}
                            <b> {product.secondAttributeKey}</b>
                            {`:${product.secondAttributeValue}`}
                          </span>

                          <p>
                            <span className='paragraph fw-bold pe-1'>{QUANTITY_PRODUCT_TEXT}</span>
                            <span>{product.quantity}</span>
                          </p>
                          <p className='fw-bold'>
                            <span className='paragraph fw-bold pe-1'>{PRICE_PRODUCT_TEXT}</span>
                            <span>{`$${product.price}`}</span>
                          </p>
                        </Col>
                      </td>
                      <td className='text-center'>
                        <span>{`$${product.totalPrice}`}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot></tfoot>
              </Table>
            </Col>
          </Col>
        </Col>
      </Col>
    </Row>
  );



  /**Renderizado del componente principal */
  return (
    <>
      {isMobile ? (
        <Container fluid className='p-0 bg-secondary-5 '>
          <Container className='p-0 bg-secondary-5 '>
            <Row className='g-0 bg-primary-5 mt-2 '>
              <Col lg={12} className='p-2 bg-secondary-5 shadow rounded'>
                <Breadcrumbs />
              </Col>
            </Row>
            <Row className='g-0 bg-secondary-5 shadow rounded my-2 '>
              <Col lg={12} className='d-flex justify-content-between align-items-center px-3 mt-4 mb-3'>
                <span className='h5 text-secondary-1 mb-1'>{ORDER_TITLE_TEXT}</span>
                <span className='text-primary-4 bg-secondary-1 badge rounded-pill px-3 py-3'>
                  {ID_ORDER_TEXT}
                  {order?.ordCodeNum}
                </span>
              </Col>
              <Col className='px-3 mb-4 text-secondary-2'>{ORDER_DETAILS_TEXT}</Col>
            </Row>
            <Row className='g-0 bg-secondary-5 shadow rounded px-4  my-2'>
              <Col xs={12} className=' p-1 text-center my-3'>
                <Button className='svg-primary-4 btn-primary-icon-standard w-100' onClick={redirectNewRequest}>
                  {SEARCH_OTHER_ORDER_TEXT}
                </Button>
              </Col>
              <Col xs={12} className=' mb-3 px-1 ' >
                <Button
                  disabled={downloadOrderDisabled || isDownloading}
                  className={`${buttonVariant} w-100 py-2 text-primary-1 rounded-3 border border-1 ${downloadOrderDisabled || isDownloading ? 'disabled border-0' : ''}`}
                  onClick={handleDownload}>{'DESCARGAR ORDEN DE COMPRA '}

                </Button>
              </Col>

            </Row>

            <StatusComponent />

            <ResumeComponent />
            <OrderDetailsComponent />
          </Container>
        </Container>
      ) : (
        <Container fluid className='p-0 min-vh-75 bg-primary-5 '>
          <Container className='p-0 bg-primary-5 min-vh-90 '>
            <Row className='pt-4 g-0 bg-primary-5 pb-3 '>
              <Col lg={12} className='p-2 bg-secondary-5 shadow rounded'>
                <Breadcrumbs list={breadcrumbsList} />
              </Col>
            </Row>
            <Row className='g-0 bg-secondary-5 shadow rounded  mb-4 '>
              <Col lg={12} className='d-flex justify-content-between align-items-center px-4 mt-4 mb-2 '>
                <span className='h2 text-secondary-1 '>{ORDER_TITLE_TEXT}</span>
                <span className='h5 text-primary-4 bg-secondary-1 badge rounded-pill px-3'>
                  {ID_ORDER_TEXT} {order?.ordCodeNum}
                </span>
              </Col>
              <Col lg={6} className='px-4 mb-4 text-secondary-2'>
                {ORDER_DETAILS_TEXT}
              </Col>
            </Row>
            <Row className=' g-0 bg-secondary-5 shadow rounded px-4 mb-4 '>
              <Col className='my-5 rounded p-1'>
                <Button
                  disabled={downloadOrderDisabled || isDownloading}
                  className={`px-5 ${buttonVariant} text-primary-1 rounded-3 border border-1 ${downloadOrderDisabled || isDownloading ? 'disabled border-0' : ''}`}
                  onClick={handleDownload}>{translate('sales.download_order_text')}

                </Button>
              </Col>
              <Col className='my-5 p-1 text-end'>
                <Button className='btn-primary-icon-standard px-5' onClick={redirectNewRequest}>
                  {SEARCH_OTHER_ORDER_TEXT}
                </Button>
              </Col>
            </Row>
            <StatusComponent />
            <Row className='p-0 g-0 my-3'>
              <ResumeComponent />
            </Row>
            <OrderDetailsComponent />
          </Container>
        </Container>
      )}
      <InfoModal
        showModal={showDownloadModal}
        handleShowModal={showLoading}
        image={
          <Image
            src={DOWNLOAD_IMAGE_URL}
            width={200}
            alt='gif'
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = ErrorImage;
            }}
          />
        }
        message={<span className='p-large-bold mt-3'>{LOADING_TEXT}</span>}
      />
      {alertDownload && <AlertModal hideAlert={() => setAlertDownload(false)} type={'danger'} hide={6000} content={ERROR_DOWNLOAD_MESSAGE} />}
    </>
  );
};

export default TrackingOrderDetails;
